import { Box, IconButton, Tooltip } from '@mui/material';
import {
    CenterFocusWeak,
    LockOpenOutlined,
    LockOutlined,
    ThreeDRotation,
} from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import ApprovalMark from '~/components/Structures/ApprovalMark';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { useClearMultiSelection } from '~/hooks/structure-set/useClearMultiSelection';
import useRoi from '~/hooks/structure-set/useRoi';
import useViewportFocus from '~/hooks/useViewportFocus';
import { ICON_PX } from '~/theme/iconSizes';
import { multiSelectedRoiNumbersState } from '~/state/atoms/structureSet/roiAtoms';
import { roiPreview3DOpenState } from '~/state/atoms/viewerAtoms';

/** A row action's button: 22 px, inside the 26 px row. */
const ACTION_BUTTON_SX = { width: 22, height: 22, p: 0 };
/**
 * Class of the row actions that show on the selected row only. The row that
 * holds them shows them too while it is pointed at or holds the focus.
 */
export const ROW_ACTIONS_CLASS = 'structure-row-actions';

export default function StructureActions({
    roi,
    isSelected,
    approvalStatus,
}: {
    roi: RoiReadModel;
    isSelected: boolean;
    approvalStatus?: DICOMApprovalStatus;
}) {
    const intl = useIntl();
    const { setVisible, setRoiSelected, setLocked } = useRoi(roi);
    const { focusOnStructure, focusOnPoint } = useViewportFocus();
    const [isPreviewOpen, setPreviewOpen] = useRecoilState(
        roiPreview3DOpenState
    );
    const multiSelected = useRecoilValue(multiSelectedRoiNumbersState);
    const clearMultiSelection = useClearMultiSelection();
    // Active only when the preview is showing THIS ROI on its own. While a bulk
    // selection is shown the active row is one of several, so its button must
    // not claim the preview is that ROI's — and must narrow to it rather than
    // switch the tool off, which is the only preview control visible then
    // (action buttons show on the selected row, and on a row pointed at).
    const isShowingBulk = multiSelected.size >= 2;
    const isPreviewingThis = isSelected && isPreviewOpen && !isShowingBulk;

    const handlePreview3D = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isPreviewingThis) {
            setPreviewOpen(false);
            return;
        }
        if (!isSelected) {
            setRoiSelected();
        }
        // Asking for one ROI's preview means this ROI, not the bulk selection
        // that would otherwise take precedence in what the preview shows.
        if (isShowingBulk) {
            clearMultiSelection();
        }
        setPreviewOpen(true);
    };

    const handleFocus = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isSelected) {
            setRoiSelected();
        }
        // Only reveal the ROI once the edit target actually resolved - an
        // unresolved target must not force visibility behind a focus that
        // silently never happens.
        const focused =
            roi.isPointRoi && roi.pointCoordinatesMm
                ? focusOnPoint(roi.pointCoordinatesMm)
                : focusOnStructure(roi.roiNumber);
        if (focused && !roi.visible) {
            setVisible(true);
        }
    };

    const handleToggleLock = (e: React.MouseEvent) => {
        e.stopPropagation();
        setLocked(!roi.locked);
    };

    const renderApprovalStatus =
        approvalStatus === DICOMApprovalStatus.APPROVED ||
        approvalStatus === DICOMApprovalStatus.REJECTED;

    // A locked row must read as locked without hover or selection, so the
    // padlock sits outside the selection-gated actions container.
    const lockButton = (
        <Tooltip
            title={
                <FormattedMessage
                    id={
                        roi.locked
                            ? 'structure.unlockTooltip'
                            : 'structure.lockTooltip'
                    }
                />
            }
        >
            <IconButton
                color={roi.locked ? 'warning' : 'primary'}
                size="small"
                sx={ACTION_BUTTON_SX}
                onClick={handleToggleLock}
                data-testid={`structure-lock-button-${roi.name}`}
                aria-label={intl.formatMessage({
                    id: roi.locked ? 'structure.unlock' : 'structure.lock',
                })}
                aria-pressed={roi.locked}
            >
                {roi.locked ? (
                    <LockOutlined sx={{ fontSize: ICON_PX }} />
                ) : (
                    <LockOpenOutlined sx={{ fontSize: ICON_PX }} />
                )}
            </IconButton>
        </Tooltip>
    );

    return (
        <Box display="flex" alignItems="center" gap="2px">
            {/* Shown as the actions beside it are. A locked row keeps it
                shown: the lock has to read at a glance. */}
            <Box
                className={ROW_ACTIONS_CLASS}
                sx={{
                    display: isSelected || roi.locked ? 'flex' : 'none',
                    alignItems: 'center',
                }}
            >
                {lockButton}
            </Box>
            <Box
                className={ROW_ACTIONS_CLASS}
                data-testid={`roi-actions-${roi.name}`}
                sx={{
                    display: isSelected ? 'flex' : 'none',
                    alignItems: 'center',
                    gap: '2px',
                }}
            >
                {!roi.isPointRoi && (
                    <Tooltip
                        title={<FormattedMessage id="structure.preview3D" />}
                    >
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={handlePreview3D}
                            data-testid={`structure-preview3d-button-${roi.name}`}
                            aria-label={intl.formatMessage({
                                id: 'structure.preview3D',
                            })}
                            aria-pressed={isPreviewingThis}
                            sx={(theme) => ({
                                ...ACTION_BUTTON_SX,
                                ...(isPreviewingThis &&
                                    theme.mixins.pressedIconButton),
                            })}
                        >
                            <ThreeDRotation sx={{ fontSize: ICON_PX }} />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip
                    title={<FormattedMessage id="structure.focus" />}
                    placement="right"
                >
                    <IconButton
                        color="primary"
                        size="small"
                        onClick={handleFocus}
                        data-testid={`structure-focus-button-${roi.name}`}
                        sx={ACTION_BUTTON_SX}
                        aria-label={intl.formatMessage({
                            id: 'structure.focus',
                        })}
                    >
                        <CenterFocusWeak sx={{ fontSize: ICON_PX }} />
                    </IconButton>
                </Tooltip>
            </Box>
            {renderApprovalStatus && (
                <Box display="grid" alignItems="center" mx={0.5}>
                    <ApprovalMark status={approvalStatus} size={14} />
                </Box>
            )}
        </Box>
    );
}
